/**
 * The site's colours (themes/pollinator/styles/index.css): the pale and the
 * bright yellows, the greens, and the near-black green of the type. The same
 * set the example posts' drawn covers use (seed_example_posts).
 */
const PALETTE = ["#fdfdc1", "#f6e961", "#efd500", "#9b9d00", "#689c00", "#359b00", "#266300", "#182e00"];

const WIDTH = 1200;
const HEIGHT = 800;

export type Props = {
  /** What the picture is drawn from: the same seed always draws the same picture */
  seed: string;
  className?: string;
};

/** A small seeded random number generator (mulberry32), so a post keeps its picture. */
function randomFrom(seed: string) {
  let state = 0;
  for (let i = 0; i < seed.length; i++) state = (Math.imul(31, state) + seed.charCodeAt(i)) | 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function between(random: () => number, low: number, high: number) {
  return low + Math.floor(random() * (high - low + 1));
}

/**
 * A post's picture when it has none of its own: overlapping circles in the
 * site's colours on one of them, as the example posts' covers are drawn.
 *
 * Drawn from the post rather than stored, so a post written in admin without a
 * cover still gets one, and the same post always gets the same picture. It is
 * decoration, so it is hidden from screen readers.
 *
 * Bridge's News editor keeps a copy (src/app/news/cover-art.tsx) to show the
 * same picture there; a change to the drawing has to be made in both.
 */
export function NewsCoverArt({ seed, className }: Props) {
  const random = randomFrom(seed);
  const colours = [...PALETTE];
  for (let i = colours.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [colours[i], colours[j]] = [colours[j], colours[i]];
  }
  const [background, ...others] = colours;
  const circles = Array.from({ length: between(random, 5, 9) }, () => ({
    r: between(random, 90, 380),
    cx: between(random, -100, WIDTH + 100),
    cy: between(random, -100, HEIGHT + 100),
    fill: others[Math.floor(random() * others.length)],
    opacity: between(random, 150, 235) / 255,
  }));

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
      className={className}
      data-testid="news-cover-art"
    >
      <rect width={WIDTH} height={HEIGHT} fill={background} />
      {circles.map((circle, i) => (
        <circle key={i} {...circle} />
      ))}
    </svg>
  );
}

export default NewsCoverArt;
